import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { LogOut, Mail, MessageSquare, ShieldCheck, KeyRound } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [totalContacts, setTotalContacts] = useState(0);
  const [newContacts, setNewContacts] = useState(0);
  const [contactedCount, setContactedCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out');
    navigate('/admin/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setLoading(false); return; }

    const fetch = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/contacts?limit=200&skip=0`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setTotalContacts(list.length);
        setNewContacts(list.filter((c: any) => c.status === 'new' || !c.status).length);
        setContactedCount(list.filter((c: any) => c.status === 'contacted').length);
        setArchivedCount(list.filter((c: any) => c.status === 'archived').length);
      } catch (err: any) {
        if (err?.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          navigate('/admin/login');
        } else {
          toast.error('Failed to load dashboard.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#001a4d] text-white px-6 py-4 shadow">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#0B8A8F] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-extrabold text-lg leading-tight">Cyvantix Admin</div>
              <div className="text-white/50 text-xs">Internal dashboard</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold transition text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">

        {loading ? (
          <div className="text-gray-500 text-sm">Loading…</div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Messages', value: totalContacts, color: 'text-[#001a4d]' },
                { label: 'New', value: newContacts, color: 'text-amber-600' },
                { label: 'Contacted', value: contactedCount, color: 'text-[#0B8A8F]' },
                { label: 'Archived', value: archivedCount, color: 'text-gray-400' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="text-xs text-gray-500 mb-1">{s.label}</div>
                  <div className={`text-4xl font-extrabold ${s.color}`}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-sm font-black uppercase tracking-widest text-gray-400 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link
                  to="/admin/contacts"
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#0B8A8F]/40 hover:bg-teal-50 transition group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#001a4d] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-[#001a4d] text-sm">Contact Messages</div>
                    <div className="text-gray-500 text-xs mt-0.5">View, respond, and manage all inquiries</div>
                  </div>
                  {newContacts > 0 && (
                    <span className="ml-auto bg-amber-100 text-amber-700 text-xs font-black px-2 py-1 rounded-full">
                      {newContacts} new
                    </span>
                  )}
                </Link>

                <Link
                  to="/admin/change-password"
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#0B8A8F]/40 hover:bg-teal-50 transition"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#001a4d] flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-[#001a4d] text-sm">Change Password</div>
                    <div className="text-gray-500 text-xs mt-0.5">Update your admin credentials</div>
                  </div>
                </Link>
              </div>
            </div>

            {/* Footer note */}
            <p className="text-xs text-gray-400 text-center">
              Cyvantix Solutions · Internal use only · <MessageSquare size={10} className="inline" /> Powered by CyvaForge
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
